import { computeAgeYears, degressiveFactor, estimateJumpsFromAge, roundToNearest } from './gvMath';
import { MANUFACTURERS, DEPRECIATION, ADJUSTMENTS } from '../config/wingsuitConfig';

const ageDepreciationEur = (ageYears, usedBaselineEur, cfg) => {
  const { phase1_years, phase1_power, transition_value_eur, phase2_end_years, phase2_power, floor_value_eur } = cfg;

  if (ageYears <= 0) return usedBaselineEur;
  if (ageYears >= phase2_end_years) return floor_value_eur;

  if (ageYears <= phase1_years) {
    const t = ageYears / phase1_years;
    const decay = Math.pow(1 - t, phase1_power);
    return transition_value_eur + (usedBaselineEur - transition_value_eur) * decay;
  }

  // Phase 2: accelerating decline toward floor
  const t = (ageYears - phase1_years) / (phase2_end_years - phase1_years);
  const decay = Math.pow(1 - t, phase2_power);
  return floor_value_eur + (transition_value_eur - floor_value_eur) * decay;
};

export const calculateWingsuit = ({ manufacturer, model, domYear, jumps, heightCm, condition, baseUse, customNewPriceEur }) => {
  const notes = [];
  const adjustments = {};
  let newPriceEur;
  let mfrCfg = null;
  let brandKey = null;
  let mdlKey = null;

  if (customNewPriceEur) {
    newPriceEur = customNewPriceEur;
    notes.push('Custom price used. Brand and version adjustments not applied.');
  } else {
    const mfrKey = manufacturer.toLowerCase().trim();
    mdlKey = model.toLowerCase().trim();
    mfrCfg = MANUFACTURERS[mfrKey];
    if (!mfrCfg) {
      throw new Error(`Unknown wingsuit manufacturer: ${manufacturer}. Expected: ${Object.keys(MANUFACTURERS).join(', ')}`);
    }
    const modelCfg = mfrCfg.models[mdlKey];
    if (!modelCfg) {
      throw new Error(`Unknown wingsuit model: ${model}. Expected: ${Object.keys(mfrCfg.models).join(', ')}`);
    }
    newPriceEur = modelCfg.new_price_eur;
    brandKey = mfrCfg.brand_key;
  }

  // Initial used factor
  const initialUsedFactor = DEPRECIATION.initial_used_factor;
  const usedBaselineEur = newPriceEur * initialUsedFactor;

  // Version penalty
  let versionMult;
  if (customNewPriceEur) {
    versionMult = 1.0;
  } else {
    versionMult = ADJUSTMENTS.version_penalty[mdlKey] ?? ADJUSTMENTS.version_penalty._default;
  }
  const hasVersionPenalty = versionMult !== 1.0;

  // Age depreciation
  const ageCfg = DEPRECIATION.age;
  let ageYears = null;
  let ageValueEur;
  let ageFactor;

  if (domYear != null) {
    ageYears = computeAgeYears(domYear);
    ageValueEur = ageDepreciationEur(ageYears, usedBaselineEur, ageCfg);
    ageFactor = newPriceEur > 0 ? ageValueEur / newPriceEur : 0;
  } else {
    ageValueEur = usedBaselineEur;
    ageFactor = initialUsedFactor;
    notes.push('DOM unknown. Age depreciation not applied (version penalty used if available).');
  }

  // Determine primary depreciation: version vs age
  let primaryValueEur;
  let versionFactor = 1.0;

  if (hasVersionPenalty) {
    const versionValueEur = usedBaselineEur * versionMult;
    versionFactor = newPriceEur > 0 ? versionValueEur / newPriceEur : 0;

    if (ageYears != null) {
      if (versionValueEur < ageValueEur) {
        // Version dominates
        primaryValueEur = versionValueEur;
        if (ageFactor < initialUsedFactor) {
          const agePenaltyPct = 1 - (ageFactor / initialUsedFactor);
          primaryValueEur *= (1 - agePenaltyPct * 0.30);
        }
        notes.push(`Version penalty dominates (${Math.round((1 - versionMult) * 100)}%). Age contributes partially.`);
      } else {
        // Age dominates
        primaryValueEur = ageValueEur;
        const versionPenaltyPct = 1 - versionMult;
        primaryValueEur *= (1 - versionPenaltyPct * 0.30);
        notes.push(`Age dominates. Version penalty (${Math.round((1 - versionMult) * 100)}%) contributes partially.`);
      }
    } else {
      primaryValueEur = versionValueEur;
    }

    adjustments.versionPenalty = versionMult;
  } else {
    primaryValueEur = ageValueEur;
  }

  // Jump depreciation (degressive)
  const jumpCfg = DEPRECIATION.jumps;
  const jumpFloor = newPriceEur > 0 ? ageCfg.floor_value_eur / newPriceEur : 0.03;
  let jumpFactor;

  if (jumps != null) {
    jumpFactor = degressiveFactor(jumps, jumpCfg.horizon_jumps, jumpCfg.power, jumpFloor);
  } else if (ageYears != null && ageYears > 0) {
    const estimatedJumps = estimateJumpsFromAge(ageYears, jumpCfg.estimate_cap, jumpCfg.estimate_tau);
    jumpFactor = degressiveFactor(estimatedJumps, jumpCfg.horizon_jumps, jumpCfg.power, jumpFloor);
    notes.push(`Jumps unknown. Estimated ~${Math.round(estimatedJumps)} (degressive, cap ~${jumpCfg.estimate_cap}).`);
  } else {
    jumpFactor = 1.0;
    notes.push('Jumps unknown and no age data. Jump depreciation not applied.');
  }

  if (jumps != null) {
    adjustments.jumpFactor = Math.round(jumpFactor * 1000) / 1000;
  }

  // Combine primary (version/age) with jumps (worse-dominant)
  const secondaryWeight = DEPRECIATION.combination.secondary_weight;
  const primaryFactor = newPriceEur > 0 ? primaryValueEur / newPriceEur : 0;
  let valueEur;

  if (jumpFactor < primaryFactor) {
    valueEur = usedBaselineEur * jumpFactor;
    const extraPenalty = (1 - primaryFactor) * secondaryWeight;
    valueEur *= (1 - extraPenalty);
  } else {
    valueEur = primaryValueEur;
    if (jumpFactor < 1.0) {
      const jumpPenalty = (1 - jumpFactor) * secondaryWeight;
      valueEur *= (1 - jumpPenalty);
    }
  }

  const combinedFactor = newPriceEur > 0 ? valueEur / newPriceEur : 0;
  adjustments.combinedFactor = Math.round(combinedFactor * 1000) / 1000;

  // Brand attractiveness
  let brandMult;
  if (customNewPriceEur) {
    brandMult = 1.0;
  } else if (brandKey) {
    brandMult = ADJUSTMENTS.brand_attractiveness[brandKey] ?? ADJUSTMENTS.brand_attractiveness._default_manufacturer;
  } else {
    brandMult = ADJUSTMENTS.brand_attractiveness._unknown_manufacturer;
    notes.push('Unknown brand - 5% desirability penalty applied.');
  }
  if (brandMult !== 1.0) {
    adjustments.brandAttractiveness = brandMult;
  }

  // Condition
  let conditionFactor;
  if (condition != null) {
    conditionFactor = ADJUSTMENTS.condition[condition.toLowerCase().trim()] ?? ADJUSTMENTS.condition._default;
  } else {
    conditionFactor = ADJUSTMENTS.condition._default;
    notes.push("Condition unknown. Assuming 'good' (baseline).");
  }
  if (conditionFactor !== 1.0) {
    adjustments.condition = conditionFactor;
  }

  // Sizing
  let sizingMult = 1.0;
  if (heightCm != null) {
    for (const tier of ADJUSTMENTS.sizing.tiers) {
      if (tier.height_above != null && heightCm > tier.height_above) {
        sizingMult = tier.penalty;
        notes.push(`Suit sizing ${tier.label}. Penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
        break;
      }
      if (tier.height_below != null && heightCm < tier.height_below) {
        sizingMult = tier.penalty;
        notes.push(`Suit sizing ${tier.label}. Penalty ${Math.round((1 - tier.penalty) * 100)}%.`);
        break;
      }
    }
  }
  if (sizingMult !== 1.0) {
    adjustments.sizing = sizingMult;
  }

  // BASE use
  let baseMult = 1.0;
  if (baseUse) {
    baseMult = ADJUSTMENTS.base_use.penalty;
    adjustments.baseUse = baseMult;
    notes.push('Primarily used for BASE. 5% penalty applied.');
  }

  // Final value
  const totalAdjustment = brandMult * conditionFactor * sizingMult * baseMult;
  valueEur = valueEur * totalAdjustment;

  // Round to nearest 5, enforce floor
  valueEur = roundToNearest(valueEur, 5);
  valueEur = Math.max(valueEur, ageCfg.floor_value_eur);

  const retentionPct = newPriceEur > 0 ? Math.round((valueEur / newPriceEur) * 1000) / 10 : 0;

  const result = {
    valueEur,
    newPriceEur: Math.round(newPriceEur * 100) / 100,
    retentionPct,
    notes,
    adjustments,
  };

  if (ageYears != null) {
    adjustments.ageFactor = Math.round(ageFactor * 1000) / 1000;
    adjustments.ageYears = Math.round(ageYears * 10) / 10;
  }
  if (hasVersionPenalty) {
    adjustments.versionFactor = Math.round(versionFactor * 1000) / 1000;
  }

  return result;
};
